import React, { useState, useEffect } from "react";
import {
  Card,
  CardContent,
  Typography,
  Box,
  Button,
  Stack,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import { useParams, useNavigate } from "react-router-dom";
import axios from "../../api/axios";
import FormDialog from "../../components/crops/FormDialog";
import AppBreadcrumbs from "../../components/AppBreadcrumbs";
import CropImage from "../../components/crops/CropImage";
import SnackbarAlert from "../../components/SnackbarAlert";
import ConfirmDialog from "../../components/ConfirmDialog";

const CropDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [crop, setCrop] = useState(null);
  const [loading, setLoading] = useState(true);
  const [formDialogOpen, setFormDialogOpen] = useState(false);
  const [formInitialData, setFormInitialData] = useState(null);
  const [confirmOpen, setConfirmOpen] = useState(false);

  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");

  const showSnackbar = (message, severity = "success") => {
    setSnackbarMessage(message);
    setSnackbarSeverity(severity);
    setSnackbarOpen(true);
  };

  const handleEditClick = () => {
    setFormInitialData(crop);
    setFormDialogOpen(true);
  };

  const handleDeleteClick = () => {
    setConfirmOpen(true);
  };

  const handleConfirmDelete = async () => {
    try {
      await axios.delete(`/api/v1/crops/${crop.id}/`);
      showSnackbar("Crop deleted successfully");
      navigate("/crops");
    } catch (err) {
      console.error(err);
      showSnackbar("Failed to delete crop", "error");
    } finally {
      setConfirmOpen(false);
    }
  };

  const handleFormSubmit = async (formData) => {
    try {
      const response = await axios.put(
        `/api/v1/crops/${formData.id}/`,
        formData
      );
      setCrop(response.data);
      showSnackbar("Crop updated successfully");
    } catch (err) {
      console.error("Form submission error:", err);
      showSnackbar("Failed to update crop", "error");
    } finally {
      setFormDialogOpen(false);
    }
  };

  useEffect(() => {
    const fetchCrop = async () => {
      try {
        const res = await axios.get(`/api/v1/crops/${id}/`);
        setCrop(res.data);
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    fetchCrop();
  }, [id]);

  if (loading) return <p>Loading crop details...</p>;
  if (!crop) return <p>Crop not found.</p>;

  return (
    <Box maxWidth={"100%"} mx="auto" mt={4}>
      <AppBreadcrumbs baseLabel="Dashboard" baseLink="/dashboard" />
      <Card>
        <CropImage image={crop.image} crop_id={crop.id} alt={crop.name} />
        <CardContent>
          <Typography variant="h5" gutterBottom>
            {crop.name}
          </Typography>

          <Typography variant="subtitle1" color="text.secondary">
            Category: {crop.category || "N/A"}
          </Typography>

          <Typography variant="body1" mt={2}>
            {crop.description || "No description provided."}
          </Typography>

          <Typography variant="body2" mt={2} color="text.secondary">
            Created: {new Date(crop.created_at).toLocaleDateString()}
          </Typography>

          <Box
            sx={{
              height: "1px",
              backgroundColor: "#ccc",
              border: "none",
              mt: 2,
              mb: 2,
            }}
          />

          {crop.diseases && crop.diseases.length > 0 && (
            <Box mt={4}>
              <Typography variant="h6" gutterBottom>
                Common Diseases
              </Typography>
              {crop.diseases.map((disease) => (
                <Box key={disease.id} mb={2}>
                  <Typography variant="subtitle1" fontWeight="bold">
                    {disease.name}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {disease.description || "No description available."}
                  </Typography>
                </Box>
              ))}
            </Box>
          )}

          <Stack direction="row" spacing={2} mt={3}>
            <Button
              variant="contained"
              color="primary"
              onClick={handleEditClick}
            >
              Edit
            </Button>
            <Button
              variant="outlined"
              color="error"
              onClick={handleDeleteClick}
            >
              Delete
            </Button>
          </Stack>
        </CardContent>
      </Card>

      <FormDialog
        open={formDialogOpen}
        onClose={() => setFormDialogOpen(false)}
        onSubmit={handleFormSubmit}
        initialData={formInitialData}
      />

      <ConfirmDialog
        open={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        onConfirm={handleConfirmDelete}
        title="Confirm Deletion"
        description={
          <>
            Are you sure you want to delete <b>{crop?.name}</b>?
          </>
        }
      />

      <SnackbarAlert
        open={snackbarOpen}
        onClose={() => setSnackbarOpen(false)}
        severity={snackbarSeverity}
        message={snackbarMessage}
      />
    </Box>
  );
};

export default CropDetails;
